"use client";

import { useMemo } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { AvsAdditionalInformationsStep } from "@/components/backoffice/cat/vehicle-assignment/additional-informations-step/avs/additional-informations-step-content";
import { AvsClientStep } from "@/components/backoffice/cat/vehicle-assignment/client-step/avs/avs-client-content";
import VehicleIdentification from "@/components/backoffice/cat/vehicle-assignment/vehicle-identification-step/vehicle-identification-content";
import {
  AvsSalesChannelsStep,
  type SalesTypeValue,
} from "@/components/backoffice/cat/vehicle-assignment/sales-channels-step";
import { useStep } from "@/hooks/use-step-handler";
import { getAvsVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-avs-step-definitions";
import type { VehicleAssignmentFlowContext } from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";
import { AvsSaleTypeStep } from "../sale-type-step/avs/avs-sale-type-content";

function VehicleAssignmentAvsSteps({
  avsInternalAccountId = "",
  avsSalesChannel = "",
  currentKvps,
  onAvsInternalAccountIdChange,
  onAvsSalesChannelChange,
  onBeforeNextChange,
  onCanProceedChange,
}: {
  avsInternalAccountId?: string;
  avsSalesChannel?: SalesTypeValue | "";
  currentKvps?: Kvps | null;
  onAvsInternalAccountIdChange?: (value: string) => void;
  onAvsSalesChannelChange?: (value: SalesTypeValue) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const { step } = useStep();

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({
      avsInternalAccountId,
      avsSalesChannel,
      kvps: currentKvps ?? null,
    }),
    [avsInternalAccountId, avsSalesChannel, currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getAvsVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const def = visibleDefinitions[step - 1];

  if (!def) {
    return (
      <Card>
        <CardContent className="p-6 text-gray-600">
          Étape invalide (WIP)
        </CardContent>
      </Card>
    );
  }

  switch (def.id) {
    case "vehicle_identification":
      return (
        <VehicleIdentification
          onCanProceedChange={onCanProceedChange}
          onBeforeNextChange={onBeforeNextChange}
          currentKvps={currentKvps}
        />
      );
    case "client":
      return <AvsClientStep onCanProceedChange={onCanProceedChange} />;
    case "sales_channels":
      return (
        <AvsSalesChannelsStep
          value={avsSalesChannel}
          subSelectValue={avsInternalAccountId}
          onValueChange={onAvsSalesChannelChange}
          onSubSelectValueChange={onAvsInternalAccountIdChange}
          onCanProceedChange={onCanProceedChange}
        />
      );
    case "additional_informations":
      return (
        <AvsAdditionalInformationsStep
          salesChannel={avsSalesChannel}
          onCanProceedChange={onCanProceedChange}
        />
      );
    case "selling_type_avs":
      return <AvsSaleTypeStep />;
    default:
      return (
        <Card>
          <CardContent className="p-6 text-gray-600">
            Contenu étape (WIP) — {def.id}
          </CardContent>
        </Card>
      );
  }
}

export { VehicleAssignmentAvsSteps };
